/**
 * Orchestrator API Client
 *
 * Typed clients for all admin orchestrator endpoints:
 *   GET /api/admin/orchestrator/jobs
 *   GET /api/admin/orchestrator/jobs/{jobId}/trace
 *   GET /api/admin/orchestrator/queues
 *   GET /api/admin/orchestrator/cost
 *   GET /api/admin/orchestrator/health-checks
 *   GET /api/admin/orchestrator/health-checks/{id}
 */

import { authenticatedFetch } from './authenticatedFetch';
import type { RetryConfig } from './resilientFetch';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string, init?: RequestInit, retryConfig?: Partial<RetryConfig>): Promise<T> {
  const response = await authenticatedFetch(
    url,
    {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    },
    retryConfig ? { retryConfig } : undefined,
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export type JobStatus = 'Queued' | 'Running' | 'Blocked' | 'Completed' | 'Failed';

/** WS-3 item 2.1 — LLM-as-judge quality gate verdict. Null on pre-gate jobs. */
export type QualityVerdict = 'passed' | 'failed_flagged' | 'skipped';

export interface AgentJobSummaryDto {
  jobId: string;
  profileId: string;
  cardId: string | null;
  outputType: string;
  status: JobStatus;
  creditsEstimated: number | null;
  creditsActual: number | null;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  /** Quality gate verdict — null on jobs that ran before the quality gate. */
  qualityVerdict: QualityVerdict | null;
  /** Fraction of rubric criteria passed (0.0–1.0); null when the gate was skipped. */
  qualityScore: number | null;
  /** Judge per-criterion failure summary when flagged; null otherwise. */
  judgeFeedback: string | null;
  /** Judge tokens spent grading — platform overhead, not user credits. */
  judgeTokens: number | null;
  /** US-24-GR-23 — LLM model id used for the run; null when unknown. */
  model: string | null;
  /** US-24-GR-23 — estimated USD cost; null = unpriced (render em-dash, not $0). */
  estimatedUsd: number | null;
  /** US-24-GR-23 — error summary (≤200 chars, server-truncated); null when no error. */
  errorSummary: string | null;
}

export interface PagedResult<T> {
  total: number;
  items: T[];
  /**
   * Work item 17433: job count per status across the WHOLE window, not just this page. Only the
   * jobs endpoint sets it; optional so every other paged reader is unaffected.
   */
  statusCounts?: Record<string, number>;
  /**
   * Work item 17433: completed / (completed + failed) across the whole window, 0..1. Null when
   * nothing in the window has reached a terminal status - which is "no answer yet", not 0%.
   * Undefined when talking to an API build that predates the field.
   */
  successRate?: number | null;
  /**
   * Work item 17426: true when the query failed, so an empty `items` means "unknown", not "none".
   * GET /health-checks used to answer a silent empty 200 on any exception, which the page rendered
   * as "No health-check sweeps recorded" - the opposite of what happened. Only the health-checks
   * response sets it today; optional so every other paged reader is unaffected.
   */
  readFailed?: boolean;
  /** Work item 17426: short operator-facing reason, present when readFailed is true. */
  readFailedReason?: string;
}

export interface GetJobsParams {
  window?: number;
  profileId?: string;
  status?: string;
  outputType?: string;
  /** Optional quality gate verdict filter: passed | failed_flagged | skipped. */
  qualityVerdict?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
}

export function getJobs(
  params: GetJobsParams = {},
  retryConfig?: Partial<RetryConfig>,
): Promise<PagedResult<AgentJobSummaryDto>> {
  const qs = buildQuery({
    window: params.window,
    profileId: params.profileId,
    status: params.status,
    outputType: params.outputType,
    qualityVerdict: params.qualityVerdict,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
    sortBy: params.sortBy,
    sortDesc: params.sortDesc !== undefined ? (params.sortDesc ? 'true' : 'false') : undefined,
  });
  return fetchJson(`/api/admin/orchestrator/jobs${qs}`, undefined, retryConfig);
}

// ---------------------------------------------------------------------------
// Jobs — CSV export (US-17-GR-29)
// ---------------------------------------------------------------------------

/** Same filter surface as GetJobsParams minus page/pageSize (export is unpaginated). */
export type ExportJobsParams = Omit<GetJobsParams, 'page' | 'pageSize'>;

export interface JobsExportResult {
  blob: Blob;
  /** True when the server capped the export at its row limit. */
  truncated: boolean;
  /** Row count reported by the server, when available (X-Export-Row-Count). */
  rowCount: number | null;
  /**
   * Total rows matching the filter server-side (before the export cap was
   * applied), when available (X-Export-Total-Matched). Used to phrase the
   * truncation toast as "N of M matching rows" instead of just "N rows".
   */
  totalMatched: number | null;
}

/**
 * GET /api/admin/orchestrator/jobs/export — text/csv attachment, filtered by
 * the same params as getJobs (minus paging). Truncation is signalled via the
 * `X-Export-Truncated: true` response header; row count (if present) via
 * `X-Export-Row-Count`; total matched rows (if present) via
 * `X-Export-Total-Matched`.
 */
export async function exportJobsCsv(params: ExportJobsParams = {}): Promise<JobsExportResult> {
  const qs = buildQuery({
    window: params.window,
    profileId: params.profileId,
    status: params.status,
    outputType: params.outputType,
    qualityVerdict: params.qualityVerdict,
    sortBy: params.sortBy,
    sortDesc: params.sortDesc !== undefined ? (params.sortDesc ? 'true' : 'false') : undefined,
  });
  const response = await authenticatedFetch(`/api/admin/orchestrator/jobs/export${qs}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  const truncated = response.headers.get('X-Export-Truncated') === 'true';
  const rowCountHeader = response.headers.get('X-Export-Row-Count');
  const parsedRowCount = rowCountHeader !== null ? Number(rowCountHeader) : NaN;
  const totalMatchedHeader = response.headers.get('X-Export-Total-Matched');
  const parsedTotalMatched = totalMatchedHeader !== null ? Number(totalMatchedHeader) : NaN;
  const blob = await response.blob();
  return {
    blob,
    truncated,
    rowCount: Number.isFinite(parsedRowCount) ? parsedRowCount : null,
    totalMatched: Number.isFinite(parsedTotalMatched) ? parsedTotalMatched : null,
  };
}

export interface AgentJobStepDto {
  stepNumber: number;
  toolName: string;
  success: boolean;
  durationMs: number;
  inputTokens: number | null;
  outputTokens: number | null;
  promptText: string | null;
  responseText: string | null;
  toolInput: string | null;
  toolOutput: string | null;
  errorMessage: string | null;
}

export interface DeliverableInfo {
  url: string;
  previewText: string;
}

export interface JobTraceResult {
  summary: AgentJobSummaryDto;
  steps: AgentJobStepDto[];
  deliverable: DeliverableInfo | null;
}

export function getJobTrace(jobId: string): Promise<JobTraceResult> {
  return fetchJson(`/api/admin/orchestrator/jobs/${encodeURIComponent(jobId)}/trace`);
}

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

export interface QueueStats {
  depth: number;
  dlqDepth: number;

  /**
   * Work item 16305: true when the active-depth read threw, so `depth` is a placeholder `0` rather
   * than a measurement. A placeholder 0 sits below every alert threshold, which is why an unknown
   * depth must never be allowed to render as a healthy tile.
   *
   * Optional because the field is additive — a response from an API that predates 16305 simply
   * omits it, and `undefined` is correctly falsy.
   */
  depthReadFailed?: boolean;

  /** As {@link depthReadFailed}, for `dlqDepth`. */
  dlqDepthReadFailed?: boolean;
}

export interface DlqDepthPoint {
  timestamp: string;
  tasksDlq: number;
  eventsDlq: number;
}

export interface DeadLetterDto {
  messageId: string;
  deadLetteredAt: string;
  queue: string;
  messageType: string;
  jobId: string | null;
  deadLetterReason: string | null;
  deadLetterErrorDescription: string | null;
  bodyPreview: string;
  fullBody: string | null;
}

// ---------------------------------------------------------------------------
// DLQ Redrive
// ---------------------------------------------------------------------------

export interface RedriveDlqRequest {
  queue: string;
  messageId: string;
}

export interface RedriveDlqResponse {
  success: boolean;
  alreadyRedriven: boolean;
  newCorrelationId: string;
  errorMessage?: string;
}

export async function redriveDlqMessage(
  queue: string,
  messageId: string,
): Promise<RedriveDlqResponse> {
  const body: RedriveDlqRequest = { queue, messageId };
  const response = await authenticatedFetch('/api/admin/orchestrator/dlq/redrive', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  return response.json() as Promise<RedriveDlqResponse>;
}

export interface QueuesResponse {
  tasks: QueueStats;
  events: QueueStats;
  activeWorkerSessions: number | null;
  dlqDepthHistory: DlqDepthPoint[];
  recentDeadLetters: DeadLetterDto[];

  /**
   * Work item 16305: true when at least one dead-letter peek threw, so `recentDeadLetters` is
   * unreliable rather than merely empty.
   *
   * Work item 16237 could already infer a failed read from a non-zero depth beside an empty list,
   * because depth comes from a different call — and said in its own comment that it could not
   * catch a failed peek on a genuinely EMPTY queue, and must not pretend to. This is the server
   * saying so directly, which closes that gap.
   */
  deadLetterReadFailed?: boolean;

  /**
   * Work item 16305: true when a peek returned a genuine prefix of a longer list. Distinct from
   * {@link deadLetterReadFailed}: the entries present are real, there are simply more that were
   * not read.
   */
  deadLetterReadPartial?: boolean;
}

export function getQueues(retryConfig?: Partial<RetryConfig>): Promise<QueuesResponse> {
  return fetchJson('/api/admin/orchestrator/queues', undefined, retryConfig);
}

// ---------------------------------------------------------------------------
// Cost
// ---------------------------------------------------------------------------

export type CostGroupBy = 'provider' | 'user' | 'outputType' | 'time';

export interface CostRow {
  key: string;
  label?: string;
  runs: number;
  inputTokens: number;
  outputTokens: number;
  estimatedUsd: number;
  creditsCharged: number | null;
  /**
   * Judge (quality gate) tokens attributed to this group — platform overhead,
   * not user credits. Populated for user/outputType/time; null for provider.
   */
  judgeTokens?: number | null;
}

export interface CostResponse {
  groupBy: CostGroupBy;
  window: string;
  totalEstimatedUsd: number;
  totalCreditsCharged: number | null;
  /** Total judge (quality gate) tokens in the window — platform overhead, not billed to users. */
  totalJudgeTokens?: number;
  rows: CostRow[];
  _ledgerEmpty?: boolean;
  /** True when the ledger-sum read failed; absent on older API builds, so test by falsiness. */
  _ledgerUnavailable?: boolean;
  _unpricedModels?: string[];
}

export function getCost(
  params: { window: number; groupBy: CostGroupBy },
  retryConfig?: Partial<RetryConfig>,
): Promise<CostResponse> {
  const qs = buildQuery({ window: params.window, groupBy: params.groupBy });
  return fetchJson(`/api/admin/orchestrator/cost${qs}`, undefined, retryConfig);
}

// ---------------------------------------------------------------------------
// Health Checks
// ---------------------------------------------------------------------------

export interface AgentHealthCheckRunDto {
  id: number;
  runAt: string;
  durationMs: number;
  /**
   * Alerts actually SENT this run for each check — NOT the number of cards found.
   * A card inside its alert cooldown (see `IsAlertDue`) contributes nothing here even
   * though it is still stuck/unreviewed/overdue. Use the `*FoundCount` fields below for
   * "is this run actually clean" decisions (Bug 15179).
   */
  stuckInProgressCount: number;
  unreviewedReadyCount: number;
  overduePendingCount: number;
  /** Cards actually found by this check, regardless of whether an alert was sent (Bug 15179). */
  stuckInProgressFoundCount: number;
  unreviewedReadyFoundCount: number;
  overduePendingFoundCount: number;
  orphanedJobsCount: number;
  pendingStatusRetriesCount: number;
  blockedRemindersCount: number;
  creditDeductionRetriesCount: number;
  notificationsSentCount: number;
}

export interface HealthCheckDetail {
  stuckInProgress: string[];
  unreviewedReady: string[];
  overduePending: string[];
  orphanedJobs: string[];
  pendingStatusRetries: string[];
  blockedReminders: string[];
  creditDeductionRetries: string[];
}

export interface HealthCheckDetailResult {
  run: AgentHealthCheckRunDto;
  details: HealthCheckDetail;
}

export interface GetHealthChecksParams {
  window?: number;
  page?: number;
  pageSize?: number;
}

export function getHealthChecks(params: GetHealthChecksParams = {}): Promise<PagedResult<AgentHealthCheckRunDto>> {
  const qs = buildQuery({
    window: params.window,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });
  return fetchJson(`/api/admin/orchestrator/health-checks${qs}`);
}

export function getHealthCheckDetail(id: number): Promise<HealthCheckDetailResult> {
  return fetchJson(`/api/admin/orchestrator/health-checks/${id}`);
}

// ---------------------------------------------------------------------------
// Review Outcomes (WS-5b / rubric N)
//
// Trust-phase analytics for the human-review loop. Grouped by (outputType,
// taskType), each row reports approve/edit/reject counts, the approve-without-
// edit rate, average quality score, and the trust phase the agent has earned
// for that slice.
// ---------------------------------------------------------------------------

export type TrustPhase = 'suggestion' | 'drafting' | 'autonomy';

export interface ReviewOutcomeStatsDto {
  outputType?: string | null;
  taskType?: string | null;
  totalReviews: number;
  approveCount: number;
  editCount: number;
  rejectCount: number;
  /** Fraction 0..1 of reviews approved with no edits. */
  approveWithoutEditRate: number;
  averageQualityScore?: number | null;
  trustPhase: TrustPhase;
}

export interface ReviewOutcomeStatsResponse {
  windowDays: number;
  totalReviews: number;
  items: ReviewOutcomeStatsDto[];
}

export function getReviewOutcomeStats(window: number): Promise<ReviewOutcomeStatsResponse> {
  const qs = buildQuery({ window });
  return fetchJson(`/api/admin/orchestrator/review-outcomes${qs}`);
}

// ---------------------------------------------------------------------------
// Credit Ledger Adjustment
// ---------------------------------------------------------------------------

export interface AdjustCreditsRequest {
  profileId: string;
  amount: number;
  reason: string;
  referencedJobId?: string;
}

export interface AdjustCreditsResponse {
  newBalance: number;
  ledgerRowId: string;
  affectedBalance: string;
}

/**
 * Thrown when the API responds 400 with a message indicating the balance would
 * go below zero (underflow / insufficient credits).
 */
export class InsufficientBalanceError extends Error {
  constructor(message = 'Adjustment would result in a negative balance') {
    super(message);
    this.name = 'InsufficientBalanceError';
  }
}

/**
 * Thrown when the API responds 404 — the profileId was not found.
 */
export class ProfileNotFoundError extends Error {
  constructor(profileId: string) {
    super(`Profile not found: ${profileId}`);
    this.name = 'ProfileNotFoundError';
  }
}
/**
 * Thrown when the API refuses an adjustment for breaching a ratified credit ceiling
 * (work item 17267, DEC-2026-09-03). Carries the API's own sentence, which names the ceiling
 * and the figure, so the operator is told why rather than that "validation" failed.
 */
export class CreditCeilingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CreditCeilingError';
  }
}

/**
 * The API answers a rejected adjustment with a JSON body of the shape { "Message": "..." }.
 * Pull that sentence out so it can be shown as prose. Falls back to the raw body when it is not
 * JSON, so nothing is ever swallowed.
 */
function parseApiMessage(body: string): string {
  try {
    const parsed = JSON.parse(body) as { Message?: string; message?: string };
    return parsed.Message ?? parsed.message ?? body;
  } catch {
    return body;
  }
}


export async function adjustCredits(
  req: AdjustCreditsRequest,
): Promise<AdjustCreditsResponse> {
  const response = await authenticatedFetch('/api/admin/orchestrator/credits/adjust', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Adjust-Confirm': 'true',
    },
    body: JSON.stringify(req),
  });

  if (response.status === 400) {
    const text = await response.text().catch(() => '');
    // Underflow: API returns a message about current balance
    // Work item 17267: a ceiling refusal is not a generic validation failure. Surface the API's
    // own sentence, which names the ceiling and the figure that breached it (DEC-2026-09-03).
    const ceilingMessage = parseApiMessage(text);
    if (/ceiling/i.test(ceilingMessage)) {
      throw new CreditCeilingError(ceilingMessage);
    }

    if (/balance|insufficient|underflow/i.test(text)) {
      throw new InsufficientBalanceError(text || undefined);
    }
    // Header-missing validation (should never occur — we always send the header)
    throw new Error(`Validation error (400): ${text || response.statusText}`);
  }

  if (response.status === 403) {
    throw new Error('Not authorised to adjust credits (403)');
  }

  if (response.status === 404) {
    throw new ProfileNotFoundError(req.profileId);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  return response.json() as Promise<AdjustCreditsResponse>;
}

// ---------------------------------------------------------------------------
// Cancel Job
// ---------------------------------------------------------------------------

export interface CancelJobRequest {
  reason: string;
}

export interface CancelJobResponse {
  success: boolean;
  alreadyCancelled: boolean;
  jobId: string;
  status: string;
  refundAmount?: number;
}

/**
 * Thrown when the API responds with 409 (job is already in a terminal state).
 * Callers should catch this and show an info toast instead of an error toast.
 */
export class JobAlreadyTerminalError extends Error {
  constructor(message = 'Job is already in a terminal state') {
    super(message);
    this.name = 'JobAlreadyTerminalError';
  }
}

export async function cancelJob(
  jobId: string,
  reason: string,
): Promise<CancelJobResponse> {
  const body: CancelJobRequest = { reason };
  const response = await authenticatedFetch(
    `/api/admin/orchestrator/jobs/${encodeURIComponent(jobId)}/cancel`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );

  if (response.status === 409) {
    throw new JobAlreadyTerminalError();
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  return response.json() as Promise<CancelJobResponse>;
}

// ---------------------------------------------------------------------------
// Threshold Alerts (US-24-GR-27)
// ---------------------------------------------------------------------------

export interface AlertThresholdConfig {
  queueDepthWarn: number;
  queueDepthAlert: number;
  dlqDepthWarn: number;
  dlqDepthAlert: number;
  eventsQueueDepthWarn: number;
  eventsQueueDepthAlert: number;
  eventsDlqDepthWarn: number;
  eventsDlqDepthAlert: number;
  successRateFloorPct: number;
  windowMinutes: number;
}

export function getAlertThresholds(): Promise<AlertThresholdConfig> {
  return fetchJson('/api/admin/orchestrator/alert-thresholds');
}

export async function putAlertThresholds(
  config: AlertThresholdConfig,
): Promise<AlertThresholdConfig> {
  const response = await authenticatedFetch('/api/admin/orchestrator/alert-thresholds', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });

  if (response.status === 400) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'One or more threshold values are out of range');
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  return response.json() as Promise<AlertThresholdConfig>;
}

export type AlertType = 'QueueDepth' | 'DlqDepth' | 'SuccessRateFloor';
export type AlertSeverity = 'Warn' | 'Alert';

export interface ThresholdAlert {
  id: number;
  alertType: AlertType;
  severity: AlertSeverity;
  metricValue: number;
  thresholdValue: number;
  message: string;
  raisedAtUtc: string;
  recoveredAtUtc?: string | null;
  acknowledgedAtUtc?: string | null;
  acknowledgedBy?: string | null;
  isOpen: boolean;
}

export interface GetAlertsParams {
  page?: number;
  pageSize?: number;
  /** Filter to only-open (unacknowledged/unresolved) alerts when true. */
  isOpen?: boolean;
}

export interface GetAlertsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: ThresholdAlert[];
}

export function getAlerts(params: GetAlertsParams = {}): Promise<GetAlertsResponse> {
  const qs = buildQuery({
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
    isOpen: params.isOpen === undefined ? undefined : String(params.isOpen),
  });
  return fetchJson(`/api/admin/orchestrator/alerts${qs}`);
}

export interface AcknowledgeAlertResponse {
  success: boolean;
  alertId: number;
  acknowledgedAtUtc: string;
}

/**
 * Thrown when the API responds with 404 (alert id not found).
 */
export class AlertNotFoundError extends Error {
  constructor(alertId: number) {
    super(`Alert not found: ${alertId}`);
    this.name = 'AlertNotFoundError';
  }
}

export async function acknowledgeAlert(alertId: number): Promise<AcknowledgeAlertResponse> {
  const response = await authenticatedFetch(
    `/api/admin/orchestrator/alerts/${encodeURIComponent(String(alertId))}/acknowledge`,
    { method: 'POST' },
  );

  if (response.status === 404) {
    throw new AlertNotFoundError(alertId);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  return response.json() as Promise<AcknowledgeAlertResponse>;
}
